import {
  IInitialViewpoint,
  IViewpointBimFile,
  IViewpointComponent,
  ITopicPoint,
} from "Data/interfaces/Activities/IViewpointData";
import {
  bcfDirectionToViewer,
  bcfLengthToViewer,
  bcfPlaneToViewer,
  bcfPointToViewer,
  placementFromForgeModel,
} from "./viewpointCameraSpace";

type ModelDictionary = Map<string, Autodesk.Viewing.Model>;

/**
 * Os `BimFiles` de um viewpoint listam TODOS os modelos que estavam carregados
 * na hora da captura, cada um com o seu `IsVisible` — inclusive os que estavam
 * desligados. Quem restaura precisa carregar só os visíveis: carregar o resto
 * para escondê-lo em seguida (o que `restoreBimFiles` faz) baixa modelo à toa e,
 * pior, faz a próxima captura nascer com uma lista ainda maior.
 *
 * `IsVisible` ausente conta como visível — BCF de outras ferramentas pode omitir.
 */
export const visibleBimFiles = (bimFiles?: IViewpointBimFile[] | null): IViewpointBimFile[] => (
  (bimFiles || []).filter((file) => file?.IsVisible !== false)
);

export class ViewpointRestoreService {
  private static normalizeUrn(raw?: string | null): string | null {
    if (!raw || raw.trim().length === 0) {
      return null;
    }
    return raw.startsWith("urn:") ? raw : `urn:${raw}`;
  }

  private static stripUrnPrefix(raw: string): string {
    return raw.replace(/^urn:/i, "");
  }

  private static collectRequiredModelUrns(viewpoint: IInitialViewpoint): Set<string> {
    const urns = new Set<string>();

    // Só os visíveis: os invisíveis nem serão carregados, então esperar por eles
    // seguraria o restore até o timeout de 10s.
    visibleBimFiles(viewpoint.BimFiles).forEach((file) => {
      const normalized =
        ViewpointRestoreService.normalizeUrn(file.Reference) ??
        ViewpointRestoreService.normalizeUrn(file.Filename);

      if (normalized) {
        urns.add(normalized);
      }
    });

    return urns;
  }

  private static waitForRequiredModels(
    viewer: Autodesk.Viewing.GuiViewer3D,
    viewpoint: IInitialViewpoint,
    onReady: () => void,
  ): void {
    const requiredUrns = ViewpointRestoreService.collectRequiredModelUrns(viewpoint);
    const GEOMETRY_LOADED_EVENT =
      (Autodesk as any)?.Viewing?.GEOMETRY_LOADED_EVENT ?? Autodesk?.Viewing.GEOMETRY_LOADED_EVENT;
    const MODEL_COMPLETED_EVENT =
      (Autodesk as any)?.Viewing?.MODEL_COMPLETED_EVENT ??
      (Autodesk as any)?.Viewing?.OBJECT_TREE_CREATED_EVENT ??
      Autodesk?.Viewing.OBJECT_TREE_CREATED_EVENT;

    const hasAllRequiredModels = () => {
      const allModels =
        (viewer.getAllModels && viewer.getAllModels()) ||
        (viewer.model ? [viewer.model] : []);

      if (!allModels || allModels.length === 0) {
        return false;
      }

      const normalizedLoaded = new Set<string>();
      const strippedLoaded = new Set<string>();
      const allModelsLoaded = allModels.every((model: Autodesk.Viewing.Model) => {
        const loaderDone =
          typeof (model as any).isLoadDone === "function"
            ? (model as any).isLoadDone()
            : true;
        return loaderDone;
      });

      allModels.forEach((model) => {
        const data = model?.getData?.();
        const modelUrn = data?.urn ?? (model as any)?.loader?.svfUrn;
        const normalized = ViewpointRestoreService.normalizeUrn(modelUrn);
        if (normalized) {
          normalizedLoaded.add(normalized);
          strippedLoaded.add(ViewpointRestoreService.stripUrnPrefix(normalized));
        }
      });

      if (requiredUrns.size === 0) {
        return normalizedLoaded.size > 0 && allModelsLoaded;
      }

      return Array.from(requiredUrns).every((urn) => {
        const normalized = ViewpointRestoreService.normalizeUrn(urn);
        if (!normalized) {
          return true;
        }
        const stripped = ViewpointRestoreService.stripUrnPrefix(normalized);
        return normalizedLoaded.has(normalized) || strippedLoaded.has(stripped);
      });
    };

    if (hasAllRequiredModels()) {
      onReady();
      return;
    }

    let resolved = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const cleanup = () => {
      if (GEOMETRY_LOADED_EVENT !== undefined) {
        viewer.removeEventListener(GEOMETRY_LOADED_EVENT, handleModelEvent);
      }
      if (MODEL_COMPLETED_EVENT !== undefined) {
        viewer.removeEventListener(MODEL_COMPLETED_EVENT, handleModelEvent);
      }
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      }
    };

    const handleModelEvent = () => {
      if (resolved || !hasAllRequiredModels()) {
        return;
      }

      resolved = true;
      cleanup();
      onReady();
    };

    if (GEOMETRY_LOADED_EVENT !== undefined) {
      viewer.addEventListener(GEOMETRY_LOADED_EVENT, handleModelEvent);
    }
    if (MODEL_COMPLETED_EVENT !== undefined) {
      viewer.addEventListener(MODEL_COMPLETED_EVENT, handleModelEvent);
    }

    timeoutId = setTimeout(() => {
      if (resolved) {
        return;
      }
      resolved = true;
      cleanup();
      onReady();
    }, 10000);
  }

  static applyViewpoint(viewer: Autodesk.Viewing.GuiViewer3D, viewpoint: IInitialViewpoint, topicPoints?: ITopicPoint[]): void {
    if (!viewer || !viewpoint) {
      console.warn("Viewer or viewpoint is null/undefined");
      return;
    }

    const runRestore = () => {
      const THREE = (window as any).THREE || (Autodesk as any).THREE;
      if (!THREE) {
        console.warn("THREE namespace not available. Skipping viewpoint restore.");
        return;
      }

      const modelMap = this.buildModelDictionary(viewer);
      this.restoreCamera(viewer, viewpoint, topicPoints, false); // animated fly-to

      // Re-apply camera whenever additional models finish loading.
      // Forge auto-fits the view on each GEOMETRY_LOADED_EVENT, which overwrites the
      // viewpoint camera when multiple files load in parallel (only 1 may be done at
      // the time applyViewpoint first runs). We listen for up to MAX_REAPPLY events
      // or 15 seconds, whichever comes first.
      const GEOM_EVENT =
        (Autodesk as any).Viewing?.GEOMETRY_LOADED_EVENT ??
        Autodesk.Viewing.GEOMETRY_LOADED_EVENT;
      let reapplyCount = 0;
      const MAX_REAPPLY = 10;

      const removeReapplyListener = () => {
        viewer.removeEventListener(GEOM_EVENT, onSubsequentModelLoad);
        clearTimeout(reapplyCleanupTimer);
      };

      const onSubsequentModelLoad = () => {
        reapplyCount++;
        if (reapplyCount > MAX_REAPPLY) {
          removeReapplyListener();
          return;
        }
        // Delay the re-apply to let async operations triggered by GEOMETRY_LOADED_EVENT
        // (e.g. AEC.LevelsExtension loading inside getLoadedChanged) settle before we
        // restore the camera. Without this delay, our listener fires during the event
        // but the extension finishes loading afterward and resets the camera again.
        setTimeout(() => {
          ViewpointRestoreService.restoreCamera(viewer, viewpoint, topicPoints);
        }, 300);
      };

      viewer.addEventListener(GEOM_EVENT, onSubsequentModelLoad);
      const reapplyCleanupTimer = setTimeout(removeReapplyListener, 15000);

      this.restoreVisibility(viewer, viewpoint.Visibility, modelMap);

      // When DefaultVisibility=false (hideAll mode), BCF-imported selection components
      // may not be listed in the visibility exceptions and would remain hidden.
      // Force-show them using the same IfcGuid resolution path.
      if (viewpoint.Visibility && !viewpoint.Visibility.DefaultVisibility) {
        const ifcOnlySelected = (viewpoint.Selection ?? []).filter(
          c => !c.ComponentDetails?.ModelUrn && !!c.IfcGuid,
        );
        if (ifcOnlySelected.length > 0) {
          const allModels = viewer.getAllModels ? viewer.getAllModels() : [viewer.model].filter(Boolean);
          this.resolveAndApplyIfcVisibility(viewer, ifcOnlySelected, allModels, false /* force show */);
        }
      }

      this.restoreSelection(viewer, viewpoint.Selection, modelMap);
      this.restoreBimFiles(viewer, viewpoint.BimFiles, modelMap);

      viewer.impl.sceneUpdated(true);
      viewer.impl.invalidate(true, true, true);

      // Wait for camera to settle before applying clipping planes
      // Camera adjustments can remove planes if applied too early
      let cameraSettleTimeout: ReturnType<typeof setTimeout> | null = null;
      const CAMERA_SETTLE_DELAY = 200;
      
      const applyClippingPlanesAfterCameraSettles = () => {
        this.restoreClippingPlanes(viewer, viewpoint.ClippingPlanes);
        viewer.removeEventListener(Autodesk?.Viewing.CAMERA_CHANGE_EVENT, handleCameraChange);
      };
      
      const handleCameraChange = () => {
        if (cameraSettleTimeout) {
          clearTimeout(cameraSettleTimeout);
        }
        cameraSettleTimeout = setTimeout(applyClippingPlanesAfterCameraSettles, CAMERA_SETTLE_DELAY);
      };
      
      viewer.addEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, handleCameraChange);
      
      setTimeout(() => {
        if (cameraSettleTimeout) {
          clearTimeout(cameraSettleTimeout);
        }
        viewer.removeEventListener(Autodesk?.Viewing.CAMERA_CHANGE_EVENT, handleCameraChange);
        this.restoreClippingPlanes(viewer, viewpoint.ClippingPlanes);
      }, 3000);
    };

    ViewpointRestoreService.waitForRequiredModels(viewer, viewpoint, runRestore);
  }

  private static buildModelDictionary(viewer: Autodesk.Viewing.GuiViewer3D): ModelDictionary {
    const models = viewer.getAllModels ? viewer.getAllModels() : [viewer.model];
    const dictionary: ModelDictionary = new Map();

    if (!models) {
      return dictionary;
    }

    models.forEach((model) => {
      const urn = model?.getData()?.urn;
      if (!urn) {
        return;
      }

      dictionary.set(urn, model);
      if (!urn.startsWith("urn:")) {
        dictionary.set(`urn:${urn}`, model);
      } else {
        dictionary.set(urn.replace(/^urn:/, ""), model);
      }
    });

    return dictionary;
  }

  /**
   * Restores the camera viewpoint using the viewer's navigation API.
   */
  private static restoreCamera(viewer: Autodesk.Viewing.GuiViewer3D, viewpoint: IInitialViewpoint, topicPoints?: ITopicPoint[], immediate = true): void {
    const cameraDto = viewpoint.Camera;
    
    if (!cameraDto) {
      console.warn("No camera data provided");
      return;
    }

    if (typeof viewer.restoreState !== "function") {
      console.error("viewer.restoreState is not available. Cannot restore viewpoint.");
      return;
    }

    const THREE = (window as any).THREE || (Autodesk as any).THREE;
    if (!THREE) {
      console.warn("THREE not available");
      return;
    }

    const allModels = viewer.getAllModels ? viewer.getAllModels() : [viewer.model].filter(Boolean);

    // O viewpoint guarda a câmera no espaço canônico do BCF — mundo do IFC, em
    // metros — venha ele da Coordly ou de qualquer outro programa. A volta é a
    // mesma conta para os dois casos, e é o inverso exato do que a captura faz.
    //
    // Não há mais convenção por origem: enquanto o load normaliza unidades
    // (`applyScaling: 'meters'`), o que a captura antiga gravava
    // (`posição + globalOffset`) já era essa mesma coordenada, então o histórico
    // continua abrindo certo. Ver `viewpointCameraSpace.test.ts`.
    const placement = placementFromForgeModel(allModels?.[0]);

    const viewerPoint = bcfPointToViewer({
      x: cameraDto.ViewPoint.X,
      y: cameraDto.ViewPoint.Y,
      z: cameraDto.ViewPoint.Z,
    }, placement);
    const position = new THREE.Vector3(viewerPoint.x, viewerPoint.y, viewerPoint.z);

    const direction = bcfDirectionToViewer({
      x: cameraDto.Direction.X, y: cameraDto.Direction.Y, z: cameraDto.Direction.Z,
    }, placement);
    const viewerDirectionVec = new THREE.Vector3(direction.x, direction.y, direction.z);

    const up = bcfDirectionToViewer({
      x: cameraDto.UpVector.X, y: cameraDto.UpVector.Y, z: cameraDto.UpVector.Z,
    }, placement);
    const viewerUpVec = new THREE.Vector3(up.x, up.y, up.z);

    // O BCF não guarda a distância até o alvo — só posição, direção e abertura.
    // O alvo aqui existe para dar pivô ao Forge (e, no modo ortográfico, ele
    // participa do zoom). Projeta-se o centro do modelo sobre o raio da câmera:
    // o pivô cai onde a câmera está de fato olhando, a uma distância que varia
    // com o viewpoint.
    //
    // Antes era `(diagonal / 2) * 0,41`, uma constante por modelo: o mesmo
    // valor para todos os viewpoints, sem relação com onde a câmera está — perto
    // demais quando a câmera estava longe, longe demais quando estava dentro.
    const modelBounds = ViewpointRestoreService.unionBoundingBox(allModels, THREE);
    let targetDistance = 100;

    if (modelBounds) {
      const size = modelBounds.getSize(new THREE.Vector3());
      const fallbackDistance = (size.length() / 2) * 0.41;
      const center = modelBounds.getCenter(new THREE.Vector3());
      // Distância do centro do modelo projetada sobre a direção de visada.
      const projected = center.clone().sub(position).dot(viewerDirectionVec);

      // Projeção negativa = o modelo está atrás da câmera; aí o centro não serve
      // de pivô e volta-se para a estimativa por tamanho.
      targetDistance = projected > 0 ? projected : fallbackDistance;
    }
    // Calculate target position from camera position + direction * distance
    const targetPosition = position.clone().add(viewerDirectionVec.clone().multiplyScalar(targetDistance));
    
    const isOrthographic = cameraDto.Type === "Orthogonal";
    const aspectRatio = cameraDto.AspectRatio && cameraDto.AspectRatio > 0 ? cameraDto.AspectRatio : 1.777;
    const fov = isOrthographic ? 45 : (cameraDto.FieldOfView || 45);

    // Use restoreState for atomic, animation-free camera restoration.
    // Mixing direct camera object mutations with navigation API calls (setView,
    // setCameraUpVector, setVerticalFov) can cause conflicts because each navigation
    // call may trigger internal state reconciliation or smooth-transition animations
    // that override the previous call. restoreState applies all properties at once
    // and the immediate=true flag skips any transition animation.
    // Os nomes das chaves seguem o schema do ViewerState do Forge
    // (@types/forge-viewer, ViewerStateOptions.viewport): `projection`,
    // `isOrthographic`, `fieldOfView` e `orthographicHeight`. Antes daqui iam
    // `fov`, `isOrtho` e `orthoScale`, que o viewer simplesmente ignora — a
    // câmera voltava na posição certa, mas sempre com o mesmo enquadramento:
    // vista ortográfica virava perspectiva e o zoom salvo em `ViewToWorldScale`
    // era descartado.
    const viewportState: Record<string, unknown> = {
      name: '',
      eye: [position.x, position.y, position.z],
      target: [targetPosition.x, targetPosition.y, targetPosition.z],
      up: [viewerUpVec.x, viewerUpVec.y, viewerUpVec.z],
      worldUpVector: [0, 0, 1],
      pivotPoint: [targetPosition.x, targetPosition.y, targetPosition.z],
      distanceToOrbit: targetDistance,
      aspectRatio,
      projection: isOrthographic ? 'orthographic' : 'perspective',
      isOrthographic,
      fieldOfView: fov,
    };

    if (isOrthographic && cameraDto.ViewToWorldScale) {
      // Guardado em metros pelo BCF; a cena pode estar em outra escala.
      viewportState.orthographicHeight = bcfLengthToViewer(cameraDto.ViewToWorldScale, placement);
    }

    // O estado final, sem animação. É esta chamada que garante o enquadramento:
    // o fly-to do Forge interpola posição e alvo, mas não leva junto o FOV nem a
    // altura ortográfica — a câmera pousa no lugar certo com o zoom que já
    // estava na tela. Por isso o primeiro viewpoint parecia certo e os
    // seguintes não: no primeiro, o `GEOMETRY_LOADED` dos modelos que ainda
    // estavam carregando reaplicava tudo em modo imediato logo depois; com o
    // modelo já carregado, ninguém corrigia.
    const settle = () => {
      (viewer as any).restoreState({ viewport: viewportState }, null, true);
      // restoreState nem sempre aplica o FOV — explícito depois. O `false`
      // preserva a posição (não reposiciona para compensar a mudança de FOV).
      if (!isOrthographic) {
        viewer.navigation.setVerticalFov(fov, false);
      }
    };

    if (immediate) {
      settle();
      return;
    }

    // Fly-to animado: anima primeiro, e só depois assenta o estado exato — mexer
    // na câmera durante a transição briga com a animação do Forge.
    (viewer as any).restoreState({ viewport: viewportState }, null, false);

    const TRANSITION_DONE =
      (Autodesk as any).Viewing?.CAMERA_TRANSITION_COMPLETED ??
      'cameraTransitionCompleted';

    let settled = false;
    const settleOnce = () => {
      if (settled) { return; }
      settled = true;
      viewer.removeEventListener(TRANSITION_DONE, settleOnce);
      settle();
    };

    viewer.addEventListener(TRANSITION_DONE, settleOnce);
    // Rede de segurança: se o evento não vier, assenta assim mesmo.
    setTimeout(settleOnce, 3000);
  }

  /**
   * Caixa que cobre todos os modelos carregados. Numa sessão federada o
   * `allModels[0]` é só o primeiro que carregou — usá-lo sozinho fazia o pivô
   * depender da ordem de carregamento.
   */
  private static unionBoundingBox(models: Autodesk.Viewing.Model[], THREE: any): any | null {
    let bounds: any = null;

    (models || []).forEach((model) => {
      const bbox = model?.getBoundingBox?.();
      if (!bbox) { return; }
      bounds = bounds ? bounds.union(bbox) : bbox.clone();
    });

    return bounds && !bounds.isEmpty?.() ? bounds : bounds;
  }

  private static restoreVisibility(
    viewer: Autodesk.Viewing.GuiViewer3D,
    visibility: IInitialViewpoint['Visibility'],
    modelMap: ModelDictionary,
  ): void {
    viewer.showAll();

    if (!visibility) {
      return;
    }

    const defaultVisible = visibility.DefaultVisibility;

    // Split exceptions into those with a resolved ModelUrn (Coordly-captured)
    // and those with only an IfcGuid (BCF-imported, no ComponentDetail in DB).
    const allExceptions = visibility.Exceptions ?? [];
    const dbIdExceptions = allExceptions.filter(c => !!c.ComponentDetails?.ModelUrn);
    // For BCF-imported: also check ComponentDetails.DbId if it's an IFC GUID string (not integer dbId)
    const ifcGuidExceptions = allExceptions.filter(c => {
      const hasModelUrn = !!c.ComponentDetails?.ModelUrn;
      const hasIfcGuid = !!c.IfcGuid;
      const hasStringDbId = !!c.ComponentDetails?.DbId && isNaN(Number(c.ComponentDetails.DbId));
      return !hasModelUrn && (hasIfcGuid || hasStringDbId);
    });

    // Apply the base visibility state.
    if (!defaultVisible) {
      viewer.hideAll();
    }

    // Apply resolved (Coordly-style) exceptions immediately.
    if (dbIdExceptions.length > 0) {
      const grouped = this.groupComponentsByModel(dbIdExceptions, modelMap);
      grouped.forEach((dbIds, model) => {
        if (dbIds.length === 0) return;
        if (defaultVisible) {
          viewer.hide(dbIds, model);
        } else {
          viewer.show(dbIds, model);
        }
      });
    }

    // Resolve IfcGuid-based (BCF-imported) exceptions asynchronously.
    if (ifcGuidExceptions.length > 0) {
      const allModels = viewer.getAllModels ? viewer.getAllModels() : [viewer.model].filter(Boolean);
      this.resolveAndApplyIfcVisibility(viewer, ifcGuidExceptions, allModels, defaultVisible);
    }
  }

  /**
   * Converts an IFC compressed GUID (22-char base-64 encoded, e.g. "20B0Uw2htYiG01000007PG")
   * to standard UUID format (e.g. "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx").
   *
   * Forge stores element identifiers in the property database (LcOaNode:LcOaNodeGuid)
   * as UUIDs, not as IFC compressed GUIDs, so this conversion is required before searching.
   */
  private static ifcGuidToUuid(ifcGuid: string): string {
    if (ifcGuid.length !== 22) return '';
    const CHARS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz_$';

    const b64 = (start: number, len: number): number => {
      let n = 0;
      for (let i = 0; i < len; i++) {
        const idx = CHARS.indexOf(ifcGuid[start + i]);
        if (idx < 0) return -1;
        n = n * 64 + idx;
      }
      return n;
    };

    // Decode into 6 numbers: 12-bit + 5×24-bit (IFC GUID encoding groups)
    const n = [b64(0, 2), b64(2, 4), b64(6, 4), b64(10, 4), b64(14, 4), b64(18, 4)];
    if (n.some(v => v < 0)) return '';

    // Repack into 4×32-bit unsigned integers (the 128-bit UUID value).
    // All arithmetic uses multiplication/division (not << / >>) to avoid
    // JavaScript's signed-32-bit truncation for values that exceed 2^31.
    const p = [
      n[0] * 1048576 + Math.floor(n[1] / 16),
      (n[1] % 16) * 268435456 + n[2] * 16 + Math.floor(n[3] / 1048576),
      (n[3] % 1048576) * 4096 + Math.floor(n[4] / 4096),
      (n[4] % 4096) * 1048576 + n[5],
    ];

    const h = (num: number, len: number): string =>
      Math.floor(num).toString(16).padStart(len, '0');

    return (
      `${h(p[0], 8)}-` +
      `${h(Math.floor(p[1] / 65536), 4)}-` +
      `${h(p[1] % 65536, 4)}-` +
      `${h(Math.floor(p[2] / 65536), 4)}-` +
      `${h(p[2] % 65536, 4)}${h(p[3], 8)}`
    );
  }

  /**
   * For BCF-imported viewpoints, visibility exceptions carry only an IfcGuid
   * (no ModelUrn / integer dbId). Converts each IfcGuid to UUID format and
   * resolves dbIds via model.getBulkProperties() on the LcOaNodeGuid property,
   * then applies show/hide across all loaded models.
   *
   * model.search() is NOT used because Forge marks internal node properties
   * (LcOaNodeGuid) as non-searchable, so search always returns 0 results.
   */
  private static resolveAndApplyIfcVisibility(
    viewer: Autodesk.Viewing.GuiViewer3D,
    exceptions: IViewpointComponent[],
    models: Autodesk.Viewing.Model[],
    defaultVisible: boolean,
  ): void {
    if (!exceptions.length || !models.length) return;

    // Extract IFC GUIDs from both IfcGuid field and ComponentDetails.DbId (when it's a string, not integer)
    const ifcGuids = exceptions.map(c => {
      const fromIfcGuid = c.IfcGuid;
      const fromDbId = c.ComponentDetails?.DbId && isNaN(Number(c.ComponentDetails.DbId)) 
        ? c.ComponentDetails.DbId 
        : null;
      return fromIfcGuid || fromDbId;
    }).filter(Boolean) as string[];
    if (!ifcGuids.length) return;

    // Convert IFC compressed GUIDs to UUID format (how Forge stores them in LcOaNodeGuid).
    const uuidSet: Record<string, boolean> = {};
    ifcGuids.forEach(g => {
      const uuid = ViewpointRestoreService.ifcGuidToUuid(g);
      if (uuid) uuidSet[uuid] = true;
    });
    // Also keep the raw 22-char IFC GUIDs — some Forge translations store GlobalId in that format.
    const rawIfcGuidSet: Record<string, boolean> = {};
    ifcGuids.forEach(g => { rawIfcGuidSet[g] = true; });

    const uuidCount = Object.keys(uuidSet).length;
    if (!uuidCount) return;

    models.forEach((model, idx) => {
      const modelUrn = (model as any).getData?.()?.urn ?? `model[${idx}]`;

      model.getObjectTree((tree: any) => {
        const allDbIds: number[] = [];
        tree.enumNodeChildren(tree.getRootId(), (dbId: number) => { allDbIds.push(dbId); }, true);

        (model as any).getBulkProperties(
          allDbIds,
          { includeInternals: true },
          (results: any[]) => {
            // Match against UUID format AND raw 22-char IFC GUID format.
            const matchedDbIds: number[] = [];
            results.forEach(result => {
              (result.properties || []).forEach((p: any) => {
                const val = String(p.displayValue);
                if (uuidSet[val] || rawIfcGuidSet[val]) {
                  matchedDbIds.push(result.dbId);
                }
              });
            });
            if (!matchedDbIds.length) {
              return;
            }
            if (defaultVisible) {
              viewer.hide(matchedDbIds, model);
            } else {
              viewer.show(matchedDbIds, model);
            }
          },
          (err: any) => {
            console.warn(`[ViewpointRestore] getBulkProperties failed on ${modelUrn}: ${err}`);
          },
        );
      }, () => {
        console.warn(`[ViewpointRestore] getObjectTree failed on ${modelUrn}`);
      });
    });
  }

  private static restoreSelection(
    viewer: Autodesk.Viewing.GuiViewer3D,
    selection: IInitialViewpoint["Selection"],
    modelMap: ModelDictionary
  ): void {
    try {
      viewer.clearSelection();
      if (typeof viewer.setAggregateSelection === 'function') {
        viewer.setAggregateSelection([]);
      }
    } catch (e) {
      console.warn("Error clearing selection:", e);
    }

    if (!selection || selection.length === 0) {
      return;
    }

    const grouped = this.groupComponentsByModel(selection, modelMap);
    const aggregate: any[] = [];

    grouped.forEach((dbIds, model) => {
      if (!Array.isArray(dbIds) || dbIds.length === 0) {
        return;
      }
      const filteredIds = dbIds.filter((id) => Number.isFinite(id));
      if (filteredIds.length === 0) {
        return;
      }
      if (model) {
        aggregate.push({ model, selection: filteredIds });
      }
    });

    if (aggregate.length > 0) {
      const validAggregate = aggregate.filter((entry) => {
        return (
          entry &&
          entry.model &&
          Array.isArray(entry.selection) &&
          entry.selection.length > 0 &&
          entry.selection.every((id: any) => Number.isFinite(id))
        );
      });
      
      if (validAggregate.length > 0) {
        try {
          setTimeout(() => {
            try {
              // Ensure proper format for setAggregateSelection - use 'ids' not 'selection'
              const formattedAggregate = validAggregate.map(({ model, selection }) => ({
                model,
                ids: Array.isArray(selection) ? selection : [selection]
              }));
              
              viewer.setAggregateSelection(formattedAggregate);
            } catch (error) {
              console.error("Error setting aggregate selection:", error);
              
              // Fallback to individual selections
              validAggregate.forEach(({ model, selection: dbIds }) => {
                try {
                  const ids = Array.isArray(dbIds) ? dbIds : [dbIds];
                  viewer.select(ids, model);
                } catch (fallbackError) {
                  console.error("Fallback selection also failed:", fallbackError);
                }
              });
            }
          }, 100);
        } catch (error) {
          console.error("Error in selection timeout:", error);
        }
      }
    }
  }

  private static restoreClippingPlanes(
    viewer: Autodesk.Viewing.GuiViewer3D,
    clippingPlanes: IInitialViewpoint["ClippingPlanes"]
  ): void {
    const THREE = (window as any).THREE || (Autodesk as any).THREE;
    if (!THREE) {
      return;
    }

    if (!clippingPlanes || clippingPlanes.length === 0) {
      (viewer.impl as any).setCutPlanes([]);
      return;
    }

    const models = viewer.getAllModels ? viewer.getAllModels() : [viewer.model].filter(Boolean);
    const placement = placementFromForgeModel(models?.[0]);

    const planes = clippingPlanes.map((plane) => {
      const { normal, constant } = bcfPlaneToViewer({
        location: { x: plane.Location.X, y: plane.Location.Y, z: plane.Location.Z },
        direction: { x: plane.Direction.X, y: plane.Direction.Y, z: plane.Direction.Z },
      }, placement);
      return new THREE.Vector4(normal.x, normal.y, normal.z, constant);
    });

    (viewer.impl as any).setCutPlanes(planes);
  }

  private static restoreBimFiles(
    viewer: Autodesk.Viewing.GuiViewer3D,
    bimFiles: IViewpointBimFile[] | undefined,
    modelMap: ModelDictionary
  ): void {
    if (!bimFiles || bimFiles.length === 0) {
      return;
    }

    bimFiles.forEach((file) => {
      const model =
        modelMap.get(file.Reference) ||
        modelMap.get(file.Reference?.replace(/^urn:/, "") || "");

      if (!model) {
        return;
      }

      const instanceTree = model.getData()?.instanceTree;
      if (!instanceTree) {
        return;
      }

      const rootId = instanceTree.getRootId();
      const visibilityManager = viewer.impl.visibilityManager as any;
      if (visibilityManager?.setNodeOff) {
        visibilityManager.setNodeOff(rootId, !file.IsVisible, model);
      }
    });
  }

  private static groupComponentsByModel(
    components: IViewpointComponent[] | undefined,
    modelMap: ModelDictionary
  ): Map<Autodesk.Viewing.Model, number[]> {
    const grouped = new Map<Autodesk.Viewing.Model, number[]>();

    if (!components) {
      return grouped;
    }

    components.forEach((component) => {
      const modelUrn = component.ComponentDetails?.ModelUrn;
      const dbIdRaw = component.ComponentDetails?.DbId || component.AuthoringToolId;
      const dbId = dbIdRaw ? Number(dbIdRaw) : NaN;

      if (!modelUrn || Number.isNaN(dbId)) {
        return;
      }

      const model =
        modelMap.get(modelUrn) || modelMap.get(modelUrn.replace(/^urn:/, ""));

      if (!model) {
        return;
      }

      const existing = grouped.get(model) || [];
      if (!existing.includes(dbId)) {
        existing.push(dbId);
      }
      grouped.set(model, existing);
    });

    return grouped;
  }
}
